import { useEffect, useSyncExternalStore } from 'react';
import { readBrainDumpSplit, createBrainDumpQuestions, type BrainDumpAsk, type PendingBrainDumpQuestion, type BrainDumpQuestionInput, type BrainDumpSplit } from '@cerebro-diem/core';
import { invokeEdgeFunction } from './edgeFunctions';
import { addPendingCapture, sendPendingCapture } from './pendingCaptures';
import { getSession } from './supabase';
import { listRawRecordings } from './rawRecordings';

/**
 * A brain dump's words split into separate thoughts, with the few that
 * deserve a follow-up question (split-brain-dump, CER-225 and CER-236).
 * Throws when no whole answer came back.
 */
export async function splitBrainDump(transcript: string, ownerId: string): Promise<BrainDumpSplit> {
  const split = readBrainDumpSplit(await invokeEdgeFunction('split-brain-dump', { transcript }, ownerId));
  if (!split) throw new Error('the brain dump came back in pieces that could not be read');
  return split;
}

/** What became of a dump's thoughts: how many the server has, and how many wait in this browser to be sent. */
export interface SavedThoughts {
  saved: number;
  waiting: number;
}

/**
 * Save each thought as an ordinary capture, the way the capture box does:
 * kept in this browser first, then sent, then filed. A thought the server
 * does not take yet waits here and is sent with the other waiting captures;
 * one whose filing call fails is filed by the server's own sweep.
 */
export async function saveThoughts(thoughts: (string | { text: string; id: string; createdAt: string })[], ownerId: string): Promise<SavedThoughts> {
  const { data: { session } } = await getSession();
  if (!session || session.user.id !== ownerId) throw new Error('The signed-in account changed; sign in as the recording owner and try again.');
  let saved = 0;
  for (const thought of thoughts) {
    const pending = await addPendingCapture(ownerId, typeof thought === 'string' ? thought : thought.text, 'voice', undefined, typeof thought === 'string' ? undefined : thought);
    if ((await sendPendingCapture(pending.id)) === 'saved') {
      saved += 1;
      // Filed now when it can be; the sweep files any whose call fails.
      void invokeEdgeFunction('classify-capture', { capture_id: pending.id }, ownerId).catch((error: unknown) =>
        console.warn('A brain dump thought was saved but not filed yet; the sweep will file it:', error),
      );
    }
  }
  return { saved, waiting: thoughts.length - saved };
}

/**
 * A brain dump's follow-up questions still to answer (CER-236), kept in this
 * browser after every answer: their thoughts are saved once answered or
 * skipped, so leaving the page loses nothing. They belong to the account
 * that made the dump.
 */
let locked = false;
const questionsStore = createBrainDumpQuestions({
  read: () => { if (!locked) throw new Error('Read requires the browser storage lock'); return localStorage.getItem('cerebro.brain-dump-questions'); },
  write: raw => localStorage.setItem('cerebro.brain-dump-questions', raw),
  remove: () => localStorage.removeItem('cerebro.brain-dump-questions'),
}, () => crypto.randomUUID());
async function withQuestionsLock<T>(action: () => T): Promise<T> {
  let entered = false;
  try {
    if (!navigator.locks) throw new Error('This browser cannot coordinate pending brain dumps.');
    return await navigator.locks.request('cerebro-brain-dump-storage', () => {
      entered = true; locked = true;
      try { return action(); } finally { locked = false; }
    });
  } catch (error) { if (!entered) questionsStore.block(); throw error; }
}
export function keepQuestions(userId: string, questions: BrainDumpQuestionInput[]) { return withQuestionsLock(() => questionsStore.keep(userId, questions)); }
export function prepareQuestion(userId: string, id: string, answer: string) { return withQuestionsLock(() => questionsStore.prepare(userId, id, answer)); }
export function dropQuestion(userId: string, id: string) { return withQuestionsLock(() => questionsStore.drop(userId, id)); }
export function rememberDump(userId: string, transcript: string, recordingId?: string) { return withQuestionsLock(() => questionsStore.rememberDump(userId, transcript, recordingId)); }
export function completeDump(userId: string, id: string, thoughts: BrainDumpQuestionInput[]) { return withQuestionsLock(() => questionsStore.completeDump(userId, id, thoughts)); }
export function retryQuestionsStorage() { return withQuestionsLock(() => questionsStore.reload()); }
export const resetQuestionsCache = questionsStore.resetCache;
export function useQuestionState() {
  const state = useSyncExternalStore(questionsStore.subscribe, questionsStore.getSnapshot);
  useEffect(() => {
    const reload = () => { void retryQuestionsStorage().catch(() => questionsStore.block()); };
    reload();
    const changed = (event: StorageEvent) => { if (event.key === 'cerebro.brain-dump-questions' || event.key === null) reload(); };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, []);
  return state;
}
export function useQuestions(userId: string | null | undefined) {
  return useQuestionState().owners.find(owner => owner.userId === userId)?.questions.filter((q): q is PendingBrainDumpQuestion & { ask: BrainDumpAsk } => q.ask !== undefined) ?? [];
}
export async function flushClearThoughts(ownerId: string) {
  await retryQuestionsStorage();
  if (questionsStore.getSnapshot().error) throw new Error(questionsStore.getSnapshot().error!);
  const entries = questionsStore.getSnapshot().owners.find(o => o.userId === ownerId)?.questions.filter(q => q.ask === undefined) ?? [];
  let waiting = 0;
  for (const thought of entries) {
    const result = await saveThoughts([{ text: thought.thought, id: thought.id, createdAt: thought.createdAt }], ownerId);
    waiting += result.waiting;
    const { data: { session } } = await getSession();
    if (!session || session.user.id !== ownerId) throw new Error('The signed-in account changed; pending thoughts remain with their recording owner.');
    await dropQuestion(ownerId, thought.id);
  }
  return { thoughts: entries.map(q => q.thought), waiting };
}
export function pendingDumps(ownerId: string) { return withQuestionsLock(() => {
  questionsStore.reload();
  const state = questionsStore.getSnapshot();
  if (state.error) throw new Error(state.error);
  return state.dumps.filter(d => d.userId === ownerId);
}); }
export async function processOwnerDump<T>(ownerId: string, action: () => Promise<T>): Promise<T> {
  if (!navigator.locks) { questionsStore.block(); throw new Error('This browser cannot coordinate pending brain dumps.'); }
  return navigator.locks.request(`cerebro-brain-dump-processing:${ownerId}`, action);
}

export function retireRecordingReceipt(ownerId: string, id: string) { return withQuestionsLock(() => questionsStore.retireRecordingReceipt(ownerId, id)); }
export async function cleanupRecordingReceipts(ownerId: string): Promise<void> {
  const receipts = await withQuestionsLock(() => questionsStore.recordingReceipts(ownerId));
  const raw = await listRawRecordings(ownerId, 'brain-dump');
  const held = new Set(raw.map(r => r.id));
  await withQuestionsLock(() => {
    for (const receipt of receipts) {
      if (!held.has(receipt.id)) questionsStore.retireRecordingReceipt(ownerId, receipt.id);
    }
  });
}
